/**
 * Central object used for Store StudentTasks module
 */
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { parseBool } from '@/utils/utils'
import { StudentActiveTab } from '@/enums/Student'
import { ActivityDrawerTab } from '@/enums/Activity'
import type { ISortItem, IStudentFilter } from '@/interfaces/api/Dara/StudentFilter'
import type { IStudentOverview } from '@/interfaces/api-resources/Dara/StudentOverview'
import type IStudentProgrammeProgress from '@/models/Student/StudentProgrammeProgress'
import type IStudentStatus from '@/models/Student/StudentStatus'
import type IStudentSenReason from '@/models/Student/SenReason'
import type IAssignLicenceDrawerLicenceType from '@/interfaces/internal-resources/AssignLicenceDrawerLicenceType'
import type { IProgrammeWithStudentProgramme } from '@/interfaces/internal-resources/ProgrammeWithStudentProgramme'
import type IStudentProgrammeAccess from '@/models/Student/StudentProgrammeAccess'
import type { StudentDetailsSections } from '@/models/Student/StudentDetailsSections'
import type IPersonalStatement from '@/interfaces/api-resources/MyUC/PersonalStatement'
import { PersonalStatementState } from '@/interfaces/api-resources/MyUC/PersonalStatement'

interface StudentState {
  activeStudentsTab: StudentActiveTab,
  activeStudentsDetailsSection: StudentDetailsSections | null,
  activityDrawerTab: ActivityDrawerTab,
  allStudentsSelected: boolean,
  assignLicence: {
    stage: number,
    studentIds: number[],
    type: IAssignLicenceDrawerLicenceType | null,
  },
  assigningLicence: boolean,
  enablingProgrammes: Set<number>,
  currentProgramme: IProgrammeWithStudentProgramme | null,
  filters: IStudentFilter,
  initialFilters: IStudentFilter,
  regeneratingReports: boolean
  licencesAssignedQueued: boolean
  loadAllAcademicYears: boolean,
  modals: {
    assignLicenceInstructions: boolean,
    changeAcademicYear: boolean,
    completeQuestionnaire: boolean,
    detailAccess: boolean,
    printReports: boolean,
    regenerateReports: boolean,
    reopenQuestionnaire: boolean,
    resetQuestionnaire: boolean,
    sendDetails: boolean,
    sendDetailsToManyStudents: boolean,
    setGroups: boolean,
    overviewSetAccess: boolean,
    enableProgramme: boolean,
    setDefaultPassword: boolean,
    setDefaultPasswords: boolean,
    setCustomPassword: boolean,
    generateMy1CSummaryReport: boolean,
    generateMyASummaryReport: boolean,
    setHasLeft: boolean,
    restoreHasLeft: boolean,
    updateGroupAndSubgroup: boolean
    setAsSen: boolean
    studentNote: boolean
  },
  myaStudentProgrammeId: number | null
  myucPersonalStatement: IPersonalStatement,
  selectedStudentId: number | null
  selectedStudents: IStudentOverview[],
  showAssignLicenceDrawer: boolean,
  showAssignLicencesDrawer: boolean,
  showEditStudentDrawer: boolean,
  showStudentsActionsDrawer: boolean,
  showStudentsFiltersDrawer: boolean,
  showTimelineAddCommentDrawer: boolean,
  studentProgrammeAccesses: IStudentProgrammeAccess,
  studentProgrammeProgresses: IStudentProgrammeProgress,
  studentStatuses: IStudentStatus
  studentSenReason: IStudentSenReason
  updatingAccess: boolean
}

const initialFilters = (currentAcademicYearId: number) => ({
  academic_year_id: currentAcademicYearId,
  programme_id: null,
  licence_type_id: null,
  year_group_id: null,
  sub_group_id: null,
  status: null,
  programme_is_hidden: null,
  programme_access: null,
  programme_progress: [],
  only_has_left: false,
  only_has_sen: false,
  include_trial: false,
  search: null,
  page: 1,
  per_page: 50,
  sort_by: [],
  my1c_favourited: null,
  my1c_favourited_area_id: null,
})

export const useStudentStore = defineStore('student', {
  state: (): StudentState => {
    const authStore = useAuthStore()
    const currentAcademicYearId = authStore.currentAcademicYear?.id ?? 0
    const initialFilterValues = initialFilters(currentAcademicYearId)

    return {
      activeStudentsTab: StudentActiveTab.OVERVIEW,
      activeStudentsDetailsSection: null,
      activityDrawerTab: ActivityDrawerTab.ACTIVITY,
      allStudentsSelected: false,
      regeneratingReports: false,
      assignLicence: {
        stage: 1,
        studentIds: [],
        type: null,
      },
      assigningLicence: false,
      enablingProgrammes: new Set(),
      currentProgramme: null,
      filters: { ...initialFilterValues },
      initialFilters: initialFilterValues,
      licencesAssignedQueued: false,
      loadAllAcademicYears: false,
      modals: {
        assignLicenceInstructions: false,
        changeAcademicYear: false,
        completeQuestionnaire: false,
        detailAccess: false,
        printReports: false,
        regenerateReports: false,
        reopenQuestionnaire: false,
        resetQuestionnaire: false,
        sendDetails: false,
        sendDetailsToManyStudents: false,
        setGroups: false,
        overviewSetAccess: false,
        enableProgramme: false,
        setDefaultPassword: false,
        setDefaultPasswords: false,
        setCustomPassword: false,
        generateMy1CSummaryReport: false,
        generateMyASummaryReport: false,
        setHasLeft: false,
        restoreHasLeft: false,
        updateGroupAndSubgroup: false,
        setAsSen: false,
        studentNote: false,
      },
      myaStudentProgrammeId: null,
      myucPersonalStatement: {
        statement: '',
        wordCount: 0,
        questions: [],
        state: PersonalStatementState.NOT_STARTED,
      },
      selectedStudentId: null,
      selectedStudents: [],
      showAssignLicenceDrawer: false,
      showAssignLicencesDrawer: false,
      showEditStudentDrawer: false,
      showStudentsActionsDrawer: false,
      showStudentsFiltersDrawer: false,
      showTimelineAddCommentDrawer: false,
      studentProgrammeAccesses: {},
      studentProgrammeProgresses: {},
      studentStatuses: {},
      studentSenReason: {},
      updatingAccess: false,
    }
  },

  getters: {
    // Use a getters to access the Stores
    appStore() {
      return useAppStore()
    },
    authStore() {
      return useAuthStore()
    },

    selectedStudentIds(state): number[] {
      return state.selectedStudents
        .filter(student => !student.is_trial)
        .map(student => student.id)
    },

    noStudentsSelected(state): boolean  {
      return !state.selectedStudents.length
    },
  },

  actions: {
    resetFilters(keepAcademicYear = false, keepProgramme = false) {
      const selectedAcademicYearId = this.filters.academic_year_id
      const selectedProgrammeId = this.filters.programme_id

      Object.assign(this.filters, this.initialFilters)

      if (keepAcademicYear) {
        this.filters.academic_year_id = selectedAcademicYearId
      }

      if (keepProgramme) {
        this.filters.programme_id = selectedProgrammeId
      }
    },

    getChangedFilters(): Partial<IStudentFilter> {
      const changedFilters: Partial<IStudentFilter> = {}

      const compareAndAssign = <K extends keyof IStudentFilter>(key: K) => {
        const filterValue = this.filters[key]
        const initialFilterValue = this.initialFilters[key]

        if (Array.isArray(filterValue) && Array.isArray(initialFilterValue)) {
          if (filterValue.length !== initialFilterValue.length) {
            changedFilters[key] = filterValue as IStudentFilter[K]
          }
        }
        else if (filterValue !== initialFilterValue && filterValue !== undefined) {
          changedFilters[key] = filterValue as IStudentFilter[K]
        }
      }

      compareAndAssign('academic_year_id')
      compareAndAssign('programme_id')
      compareAndAssign('licence_type_id')
      compareAndAssign('year_group_id')
      compareAndAssign('sub_group_id')
      compareAndAssign('status')
      compareAndAssign('programme_is_hidden')
      compareAndAssign('programme_access')
      compareAndAssign('programme_progress')
      compareAndAssign('only_has_left')
      compareAndAssign('only_has_sen')
      compareAndAssign('include_trial')
      compareAndAssign('search')
      compareAndAssign('page')
      compareAndAssign('per_page')
      compareAndAssign('sort_by')

      return changedFilters
    },

    setFiltersFromHashParams(hashParams: Record<string, string>) {
      const setIfExists = <T>(key: keyof IStudentFilter, parseFn: (value: string) => T) => {
        if (key in hashParams) {
          (this.filters[key] as T) = parseFn(hashParams[key])
        }
      }

      setIfExists<number | null>('academic_year_id', value => (value !== null ? parseInt(value) : null))
      setIfExists<number>('programme_id', parseInt)
      setIfExists<number>('licence_type_id', parseInt)
      setIfExists<number>('year_group_id', parseInt)
      setIfExists<number>('sub_group_id', parseInt)
      setIfExists<string>('status', value => value)
      setIfExists<boolean>('programme_is_hidden', parseBool)
      setIfExists<string>('programme_access', value => value)
      setIfExists<number[]>('programme_progress', value => Array.isArray(value) ? value.map(Number) : [parseInt(value)])
      setIfExists<boolean>('only_has_left', parseBool)
      setIfExists<boolean>('only_has_sen', parseBool)
      setIfExists<boolean>('include_trial', parseBool)
      setIfExists<string>('search', value => decodeURIComponent(value).replace(/\+/g, ' '))
      setIfExists<number>('page', parseInt)
      setIfExists<number>('per_page', parseInt)

      if ('sort_by' in hashParams) {
        this.filters.sort_by = Array.isArray(hashParams.sort_by)
          ? hashParams.sort_by.map(item => ({
            key: item.key,
            order: item.order === 'asc' || item.order === 'desc' ? item.order : parseBool(item.order),
          }) as ISortItem)
          : []
      }
    },

    setAcademicYearFilter(academicYearId: number) {
      this.filters.academic_year_id = academicYearId
    },

    toggleShowAssignLicenceDrawer() {
      this.showAssignLicenceDrawer = !this.showAssignLicenceDrawer
      this.assignLicence.stage = 1
    },

    toggleShowEditStudentDrawer() {
      this.showEditStudentDrawer = !this.showEditStudentDrawer
    },

    removeProgrammeProgressFilter(value: number) {
      this.filters.programme_progress = this.filters.programme_progress?.filter(v => v !== value) ?? []
    },
  },
})
